import React, { useState, useEffect } from "react";
import { TextField, InputAdornment, IconButton, Box } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch } from "react-redux";
import { searchWords, resetSearch } from "../store/wordSlice";
import { AppDispatch } from "../store/store";
import { debounce } from "lodash";
import { useLocation } from "react-router-dom";

interface SearchBarProps {
  isDarkMode: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({ isDarkMode }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const isOnFavoritesPage = location.pathname === "/favorites";

  const debouncedSearch = debounce((query: string) => {
    if (query.trim()) {
      dispatch(
        searchWords({ searchTerm: query, isFavorite: isOnFavoritesPage })
      );
      setIsSearchActive(true);
    } else {
      dispatch(resetSearch({ isFavorite: isOnFavoritesPage }));
      setIsSearchActive(false);
    }
  }, 300);

  useEffect(() => {
    if (searchTerm) {
      debouncedSearch(searchTerm);
    } else {
      dispatch(resetSearch({ isFavorite: isOnFavoritesPage }));
      setIsSearchActive(false);
    }
  }, [searchTerm, debouncedSearch, dispatch, isOnFavoritesPage]);

  const handleClearSearch = () => {
    setSearchTerm("");
    dispatch(resetSearch({ isFavorite: isOnFavoritesPage }));
    setIsSearchActive(false);
  };

  return (
    <Box sx={{ mx: 2 }}>
      <TextField
        variant="standard"
        placeholder="search..."
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          borderRadius: "50px",
          backgroundColor: isDarkMode ? "#555" : "#f0f0f0",
          width: "200px",
          height: "36px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& .MuiInputBase-input": {
            fontSize: "0.875rem",
            fontWeight: "600",
            padding: "8px 22px",
            height: "100%",
            color: "rgb(170, 170, 170)",
          },
          "& input::placeholder": {
            fontSize: "0.875rem",
            fontWeight: "500",
            color: "rgb(170, 170, 170)",
            opacity: 1,
          },
        }}
        InputProps={{
          disableUnderline: true,
          endAdornment: (
            <InputAdornment position="end">
              {isSearchActive && (
                <IconButton
                  onClick={handleClearSearch}
                  disableRipple
                  sx={{
                    padding: "5px",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                    "&:focus": {
                      outline: "none",
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <ClearIcon
                    sx={{
                      opacity: 0.5,
                      marginRight: "5px",
                      transform: "scale(0.9)",
                      transition: "opacity 0.3s ease",
                      "&:hover": {
                        opacity: 1,
                      },
                    }}
                  />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
};

export default SearchBar;
